import React from 'react';
import { Building2, Briefcase, Shield, Laptop, FileText, Clipboard, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { useTranslation } from '../hooks/useTranslation';

export const Services = () => {
  const { t } = useTranslation();
  
  const iconMap = {
    'building': Building2,
    'briefcase': Briefcase,
    'shield': Shield,
    'laptop': Laptop,
    'file-text': FileText,
    'clipboard': Clipboard
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Map services with their icons
  const serviceIcons = ['briefcase', 'building', 'shield', 'laptop', 'file-text', 'clipboard'];

  return (
    <section id="services" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="inline-block px-4 py-2 bg-navy-100 text-navy-700 rounded-full text-sm font-semibold mb-4">
            {t.services.badge}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-navy-900 mb-6">
            {t.services.title}
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            {t.services.description}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto mb-12">
          {t.services.services.map((service, index) => {
            const Icon = iconMap[serviceIcons[index]] || Building2;
            return (
              <Card 
                key={index}
                className="border-2 border-gray-200 hover:border-burgundy-400 transition-all duration-300 hover:shadow-2xl group bg-white"
              >
                <CardHeader>
                  <div className="w-16 h-16 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-2xl text-navy-900 mb-2">
                    {service.title}
                  </CardTitle>
                  <CardDescription className="text-gray-600 text-base leading-relaxed">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <p className="font-semibold text-navy-800 text-sm uppercase tracking-wide mb-3">
                      {t.services.keyFeatures}
                    </p>
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-burgundy-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Button 
                    onClick={scrollToContact}
                    className="w-full mt-6 bg-burgundy-700 hover:bg-burgundy-800 text-white"
                  >
                    {t.services.learnMore}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Call to Action */}
        <div className="max-w-4xl mx-auto bg-gradient-to-r from-burgundy-700 to-burgundy-900 rounded-2xl p-10 text-center shadow-xl">
          <h3 className="text-3xl font-bold text-white mb-4">
            {t.services.ctaTitle}
          </h3>
          <p className="text-white/90 text-lg mb-6 max-w-2xl mx-auto">
            {t.services.ctaDescription}
          </p>
          <Button 
            onClick={scrollToContact}
            size="lg"
            className="bg-white text-burgundy-900 hover:bg-gray-100 px-8 py-6 text-lg font-semibold"
          >
            {t.services.ctaButton}
          </Button>
        </div>
      </div>
    </section>
  );
};
